import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import TombolaModal from '@/components/home/_components/tombola-modal';
import TombolaMinimisedModal from '@/components/home/_components/minimised-modal';
import TombolaBaseComponent from '@/components/base/base.component';
import { ModalEventType } from '@/components/home/_components/tombola-modal/_domain';
import { IModal, MODAL_ARRANGEMENT_STEP_PIXELS, MODAL_SPAWN_EVENT_NAME, IModalSpawnConfiguration } from '@/plugins/modal-manager';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { IKeyboardShortcutService } from '@/components/chat/_domain/modules/keyboard-shortcut-service';
import homeChildComponents from '@/components/home/_components';
import { WindowType, ColourTheme, UserPreferences } from '@/app-modules/store/modules/user-preferences';
import { IModalLaunchOptions } from '@/plugins/modal-manager/models';
import { User } from '@/app-modules/store/modules/user';
import { IArcadeAdminFeedServiceProvider } from '@/components/chat/_domain/modules/arcade-admin-feed-service-provider';
import { UserSettingsUpdatedEventName } from '@/components/user-settings/_domain/models';
import { ConfigurationPaths } from '@/app-modules/config';
import { ITogglesService } from '@/app-modules/config/toggles-service';
const colourThemes = [ColourTheme.Light, ColourTheme.Dark];

@Component({
  name: 'HomeComponent',
  components: homeChildComponents
})
export default class HomeComponent extends TombolaBaseComponent {
  private modalSpawnConfiguration: IModalSpawnConfiguration = null;
  private nextModalPosition = { left: 0, top: 0 };
  private isNavigationMenuActive: boolean = this.$tombolaStore.Get(UserPreferences).pinNavigationMenu;

  private get keyboardShortcutService(): IKeyboardShortcutService {
    return this.$container.get(SERVICE_IDENTIFIERS.IKeyboardShortcutService) as IKeyboardShortcutService;
  }

  private get arcadeAdminFeedProvider(): IArcadeAdminFeedServiceProvider {
    return this.$container.get(SERVICE_IDENTIFIERS.IArcadeAdminFeedServiceProvider);
  }

  private get colourThemeCssClasses(): { [key: string]: boolean } {
    const userPreferences = this.$tombolaStore.Get(UserPreferences);
    const cssClasses: { [key: string]: boolean } = {};
    for (const theme of colourThemes) {
      cssClasses[theme] = userPreferences.colourTheme === theme;
    }
    return cssClasses;
  }

  protected get togglesService(): ITogglesService {
    return this.$container.get(SERVICE_IDENTIFIERS.ITogglesService);
  }

  private async mounted() {
    const useNewToggleEndPoint = await this.togglesService.isLive('CIC-494_UseNETToggleSystem');
    await this.togglesService.populateToggleStore(useNewToggleEndPoint);
    await this.initialiseSpawnAgent();
    this.initialiseModalSpawnHook();
    this.initialiseKeyboardShortcuts();
    this.restoreUserWindowPreferences();
    this.initialiseArcadeAdminFeedProvider();
    this.$eventbus.$on(UserSettingsUpdatedEventName, this.updatePinNavigationMenu);
  }

  private beforeDestroy() {
    this.disposeArcadeAdminFeedProvider();
    this.disableAutoRestoreAllWindows();
    this.destroyModalSpawnHook();
    this.$modalManager.destroyAllModals();
    this.$eventbus.$off(UserSettingsUpdatedEventName, this.updatePinNavigationMenu);
  }

  private updatePinNavigationMenu() {
    Vue.nextTick(() => {
      this.isNavigationMenuActive = this.$tombolaStore.Get(UserPreferences).pinNavigationMenu;
      if (this.isNavigationMenuActive) {
        this.$modalManager.adjustModalsAfterPinningNavigationMenu();
      }
    });
  }

  private async initialiseSpawnAgent(): Promise<void> {
    this.modalSpawnConfiguration = await this.$modalManager.spawnAgent.initialise();
  }

  private initialiseModalSpawnHook() {
    this.$eventbus.$on(MODAL_SPAWN_EVENT_NAME, this.spawnModal);
  }

  private destroyModalSpawnHook() {
    this.$eventbus.$off(MODAL_SPAWN_EVENT_NAME, this.spawnModal);
  }

  private async restoreUserWindowPreferences() {
    const user = this.$tombolaStore.Get(User);
    if (this.willAutoRestoreAllWindows()) {
      const attemptCsChatRestore = user.isCsMember;
      this.$modalManager.restoreAllModalsFromUserWindowPreferences(attemptCsChatRestore);
    }
    else {
      this.$modalManager.restoreCmChatModalFromUserWindowPreferences();
      if (user.isCsMember) {
        this.$modalManager.restoreCsChatModalFromUserWindowPreferences();
      }

      const config = await this.configuration();
      const autoLaunchTrainingRoom = config.getConfigurationByPath(ConfigurationPaths.settings.auto_open_training_room_name);
      if (autoLaunchTrainingRoom !== undefined && autoLaunchTrainingRoom !== '') {
        this.$modalManager.spawnAgent.trySpawnChatModalFromRoomName(autoLaunchTrainingRoom);
      }
      this.enableAutoRestoreAllWindows();
    }
  }

  private willAutoRestoreAllWindows(): boolean {
    const user = this.$tombolaStore.Get(User);
    return user.autoRestoreAllWindows;
  }

  private enableAutoRestoreAllWindows() {
    const autoRestoreAllWindowsState = true;
    this.setAutoRestoreAllWindowsState(autoRestoreAllWindowsState);
  }

  private disableAutoRestoreAllWindows() {
    const autoRestoreAllWindowsState = false;
    this.setAutoRestoreAllWindowsState(autoRestoreAllWindowsState);
    this.$modalManager.disableAutoRestoreUserPreferenceForAllModals();
  }

  private setAutoRestoreAllWindowsState(autoRestoreAllWindowsState: boolean) {
    const user = this.$tombolaStore.Get(User);
    user.autoRestoreAllWindows = autoRestoreAllWindowsState;
    this.$tombolaStore.Save(user);
  }

  private initialiseKeyboardShortcuts() {
    window.addEventListener('keyup', (event) => {
      this.keyboardShortcutService.processKeyboardEvent(event);
      if (event.key === 'F7') {
        event.preventDefault();
        this.$modalManager.hideModalShortcutIcons();
      }
    });

    window.addEventListener('keydown', (event) => {
      if (event.key === 'F7') {
        event.preventDefault();
        this.$modalManager.showModalShortcutIcons();
      }
    });
  }

  private initialiseArcadeAdminFeedProvider(): void {
    this.arcadeAdminFeedProvider.initialise();
  }

  private disposeArcadeAdminFeedProvider(): void {
    this.arcadeAdminFeedProvider.dispose();
  }

  private spawnModal(modalType: WindowType, modalTitle?: string, modalTooltip?: string, modalProps?: IModalLaunchOptions) {
    const TombolaModalClass = Vue.extend(TombolaModal);
    const instance: any = new TombolaModalClass();
    instance.modalType = modalType;
    instance.modalTitle = modalTitle;
    instance.modalTooltip = modalTooltip;
    instance.modalProps = modalProps;
    instance.container = 'workbench';
    instance.windowEventCallback = this.windowEventHandler;
    instance.$mount();
    (this.$refs.container as Node).appendChild(instance.$el);

    if (!modalProps.location) {
      const userWindowPreference = this.$modalManager.findUserWindowPreference(modalType, modalProps);
      if (userWindowPreference && userWindowPreference.location) {
        modalProps.location = Object.assign({}, userWindowPreference.location);
      }
    }

    if (modalProps.location) {
      instance.$modal.moveTo(instance.modalId, modalProps.location);
      instance.$modal.resize(instance.modalId, modalProps.location);
    }
    else {
      instance.$modal.moveTo(instance.modalId, this.nextModalPosition);
      this.setNextModalPosition();
    }
  }

  private setNextModalPosition() {
    let newModalPosition = {
      left: this.nextModalPosition.left + MODAL_ARRANGEMENT_STEP_PIXELS,
      top: this.nextModalPosition.top + MODAL_ARRANGEMENT_STEP_PIXELS
    };
    const workbenchDimenionsInPixels = this.getWorkbenchDimensionsInPixels();
    if (newModalPosition.left > (workbenchDimenionsInPixels.width * .5) || newModalPosition.top > (workbenchDimenionsInPixels.height * .5)) {
      newModalPosition = { left: 0, top: 0};
    }
    this.nextModalPosition = newModalPosition;
  }

  private getWorkbenchDimensionsInPixels(): { width: number, height: number } {
    const containerElement = this.getWorkbenchContainerElement();
    return {
      width: containerElement.clientWidth,
      height: containerElement.clientHeight
    };
  }

  private getWorkbenchContainerElement(): HTMLElement {
    return this.$refs.workbench as HTMLElement;
  }

  private spawnMinimisedModal(modalSpawned: string, modalTitle?: string, modalTooltip?: string, modalProps?: object): TombolaMinimisedModal {
    const MinimisedModalClass = Vue.extend(TombolaMinimisedModal);
    const instance: any = new MinimisedModalClass();
    instance.modalName = modalSpawned;
    instance.modalTitle = modalTitle;
    instance.modalTooltip = modalTooltip;
    instance.modalProps = modalProps;
    instance.container = 'toolbar-bottom';
    instance.minimizedModalId = modalSpawned;
    instance.windowEventCallback = this.windowEventHandler;
    instance.$mount();
    (this.$refs.bottomContainer as Node).appendChild(instance.$el);
    return instance as TombolaMinimisedModal;
  }

  private windowEventHandler(modal: IModal, action: ModalEventType): TombolaMinimisedModal {
    return this.spawnMinimisedModal(modal.modalId, modal.modalTitleText, modal.modalTooltipText);
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/home/home.component.ts